import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getLeaveTypes,
  deleteLeaveType,
  toggleLeaveTypeStatus,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaPrint,
  FaFileExcel,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import { chunkArray } from "../../../../shared/utils.js/utils";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";

const Vacations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const { isLoading, error, success } = useSelector(state => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leaveTypeToDelete, setLeaveTypeToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const lastErrorRef = useRef({ message: null, time: 0 });
  const searchTimeoutRef = useRef(null);
  const [leaveTypeForPrint, setLeaveTypeForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [allLeaveTypes, setAllLeaveTypes] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'inactive'
  const [sortField, setSortField] = useState("name"); // 'name' | 'default_balance' | 'is_active'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'
  const [singleLeaveTypeForPreview, setSingleLeaveTypeForPreview] =
    useState(null);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.hr.vacations");
  }, [setTitle]);

  const handleApplyPerPage = () => {
    // عند الضغط على تطبيق: نطبّق القيمة الجديدة ونرجع لأول صفحة
    const total = filteredData?.length || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, perPageInput));
    setPerPage(safeValue);
    setLocalCurrentPage(1);
  };

  // دالة ذكية لتقسيم البيانات بناءً على ارتفاع الصفوف المتوقع لتجنب تجاوز مساحة الصفحة
  const calculateDynamicChunks = items => {
    const tableContainer = document.getElementById(
      "vacations-measurement-container",
    );
    if (!tableContainer) {
      const fallbackChunks = [];
      for (let i = 0; i < items.length; i += 15) {
        fallbackChunks.push(items.slice(i, i + 15));
      }
      return fallbackChunks;
    }

    const thead = tableContainer.querySelector("thead");
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer.querySelectorAll("tbody tr");

    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      const rowHeight = rowElement ? rowElement.offsetHeight : 35;

      if (
        currentHeight + rowHeight > MAX_TABLE_HEIGHT &&
        currentChunk.length > 0
      ) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // Fetch leave types
  const fetchAllLeaveTypes = useCallback(async () => {
    try {
      const result = await dispatch(
        getLeaveTypes({ language: i18n.language, page: 1, per_page: 10000 }),
      ).unwrap();
      setAllLeaveTypes(result?.data || result || []);
    } catch (err) {
      console.error("Failed to fetch leave types:", err);
    }
  }, [dispatch, i18n.language]);

  useEffect(() => {
    fetchAllLeaveTypes();
  }, [fetchAllLeaveTypes]);

  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, statusFilter, sortField, sortOrder]);

  const filteredData = React.useMemo(() => {
    let data = allLeaveTypes || [];
    if (searchTerm) {
      data = data.filter(lt =>
        lt.name?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (statusFilter === "active") {
      data = data.filter(lt => lt.is_active);
    } else if (statusFilter === "inactive") {
      data = data.filter(lt => !lt.is_active);
    }
    data = [...data].sort((a, b) => {
      let aVal, bVal;
      if (sortField === "default_balance") {
        aVal = a.default_balance ?? 0;
        bVal = b.default_balance ?? 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else if (sortField === "is_active") {
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const aVal = (a.name?.toString() || "").toLowerCase();
        const bVal = (b.name?.toString() || "").toLowerCase();
        if (sortOrder === "asc")
          return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      }
    });
    return data;
  }, [allLeaveTypes, searchTerm, statusFilter, sortField, sortOrder]);

  const paginatedLeaveTypes = React.useMemo(() => {
    const startIndex = (localCurrentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, localCurrentPage, perPage]);

  const totalPages = Math.ceil(filteredData.length / perPage);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleAdd = () => {
    navigate("/vacations/add");
  };

  const handleEdit = id => {
    navigate(`/vacations/edit/${id}`);
  };

  const handleView = id => {
    navigate(`/vacations/details/${id}`);
  };

  const handleDelete = leaveType => {
    setLeaveTypeToDelete(leaveType);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (leaveTypeToDelete) {
      const idToDelete = leaveTypeToDelete.id;
      // Optimistically remove from local state first
      setAllLeaveTypes(prev =>
        prev.filter(leaveType => leaveType.id !== idToDelete),
      );
      setShowDeleteModal(false);
      setLeaveTypeToDelete(null);

      // Then call the API
      try {
        const result = await dispatch(deleteLeaveType(idToDelete));
        if (deleteLeaveType.rejected.match(result)) {
          // Revert on error - restore the deleted leave type
          setAllLeaveTypes(prev => {
            const restored = [...prev, leaveTypeToDelete];
            // Sort to maintain order if needed
            return restored.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          });
        }
      } catch (error) {
        // Revert on error
        setAllLeaveTypes(prev => {
          const restored = [...prev, leaveTypeToDelete];
          return restored.sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        });
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLeaveTypeToDelete(null);
  };

  const handleToggleStatus = async id => {
    try {
      // Optimistically update the local state first
      setAllLeaveTypes(prev =>
        prev.map(leaveType =>
          leaveType.id === id
            ? { ...leaveType, is_active: !leaveType.is_active }
            : leaveType,
        ),
      );

      // Then call the API
      const result = await dispatch(toggleLeaveTypeStatus(id));
      if (toggleLeaveTypeStatus.rejected.match(result)) {
        // Revert on error
        setAllLeaveTypes(prev =>
          prev.map(leaveType =>
            leaveType.id === id
              ? { ...leaveType, is_active: !leaveType.is_active }
              : leaveType,
          ),
        );
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      // Revert on error
      setAllLeaveTypes(prev =>
        prev.map(leaveType =>
          leaveType.id === id
            ? { ...leaveType, is_active: !leaveType.is_active }
            : leaveType,
        ),
      );
    }
  };

  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  const handleExportLeaveTypeToExcel = leaveType => {
    const data = [
      {
        [t("vacations.tableHeaders.name", { defaultValue: "اسم نوع الإجازة" })]:
          leaveType.name || "—",
        [t("vacations.tableHeaders.days", {
          defaultValue: "الرصيد الافتراضي",
        })]: leaveType.default_balance ?? leaveType.days ?? 0,
        [t("vacations.tableHeaders.status", { defaultValue: "الحالة" })]:
          leaveType.is_active
            ? t("vacations.status.active", { defaultValue: "نشط" })
            : t("vacations.status.inactive", { defaultValue: "غير نشط" }),
      },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LeaveType");
    const identifier = leaveType.name || leaveType.id;
    XLSX.writeFile(
      wb,
      `LeaveType_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success(
      t("vacations.export.success", {
        defaultValue: "تم تصدير البيانات بنجاح",
      }),
      { rtl: isRTL },
    );
  };

  // Handle print single leave type record
  const handlePrintLeaveType = async leaveType => {
    try {
      setLeaveTypeForPrint(leaveType);
      setPdfType("single");
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 400));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`leave-type-pdf-${leaveType.id}`);
      if (!element) {
        toast.error(
          t("vacations.print.error", {
            defaultValue: "خطأ في تحميل قالب الطباعة",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            `leave-type-pdf-${leaveType.id}`,
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";

            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily =
                  '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = "bold";
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/png", 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Professional solution: If content slightly exceeds one page, scale it down to fit
      // This prevents extra blank pages while maintaining quality
      const tolerance = 10; // 10mm tolerance
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        // Scale down to fit exactly on one page
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;

        // Center the scaled image
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(
          imgData,
          "PNG",
          xOffset,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else if (imgHeight <= pdfHeight) {
        // Fits perfectly on one page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else {
        // Content is too large, split across multiple pages
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(
            imgData,
            "PNG",
            0,
            -position,
            imgWidth,
            imgHeight,
            undefined,
            "FAST",
          );
          position += pdfHeight;
        }
      }

      const identifier = leaveType.name || leaveType.id;
      const blob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `ACE_LeaveType_${identifier}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Leave Type PDF error:", err);
      toast.error(
        t("vacations.print.error", { defaultValue: "حدث خطأ أثناء إنشاء PDF" }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setLeaveTypeForPrint(null);
      setPdfType(null);
    }
  };

  // Handle preview all leave types
  const handlePreviewAll = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(
        t("vacations.print.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const chunks = calculateDynamicChunks(filteredData);
    setPdfChunks(chunks);
    setPdfType("all");
    setIsPreviewOpen(true);
  };

  // Handle preview single leave type
  const handlePreviewSingle = leaveType => {
    setSingleLeaveTypeForPreview(leaveType);
    setIsSinglePreviewOpen(true);
  };

  // Handle export all leave types to Excel
  const handleExportAllToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(
        t("vacations.export.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const data = filteredData.map((leaveType, index) => ({
        [t("common.serialNumber", { defaultValue: "#" })]: index + 1,
        [t("vacations.tableHeaders.name", { defaultValue: "اسم نوع الإجازة" })]:
          leaveType.name || "—",
        [t("vacations.tableHeaders.days", {
          defaultValue: "الرصيد الافتراضي",
        })]: leaveType.default_balance ?? leaveType.days ?? 0,
        [t("vacations.tableHeaders.status", { defaultValue: "الحالة" })]:
          leaveType.is_active
            ? t("vacations.status.active", { defaultValue: "نشط" })
            : t("vacations.status.inactive", { defaultValue: "غير نشط" }),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("vacations.excel.sheetName", { defaultValue: "Leave Types" }),
      );
      const fileName = `${t("vacations.excel.fileName", { defaultValue: "LeaveTypes" })}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("vacations.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("vacations.export.error", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  // Handle print all leave types
  const handlePrintAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(
        t("vacations.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const chunks = calculateDynamicChunks(filteredData);
      setPdfChunks(chunks);
      setPdfType("all");

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);

      await new Promise(resolve => setTimeout(resolve, 500));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(2, devicePixelRatio * 1.5);

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = document.getElementById(
          `leave-types-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(
              t("vacations.print.error", {
                defaultValue: "قالب الطباعة غير متوفر",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `leave-types-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);

        if (chunkIndex > 0) {
          pdf.addPage("a4", "p");
        }

        pdf.addImage(imgData, "PNG", 0, 0, 210, 297, undefined, "FAST");
      }

      const allBlob = pdf.output("blob");
      const allBlobUrl = URL.createObjectURL(allBlob);
      const allAnchor = document.createElement("a");
      allAnchor.href = allBlobUrl;
      allAnchor.download = `ACE_LeaveTypes_All_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(allAnchor);
      allAnchor.click();
      document.body.removeChild(allAnchor);
      URL.revokeObjectURL(allBlobUrl);
      toast.success(
        t("vacations.print.success", { defaultValue: "تم تنزيل PDF بنجاح" }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Leave Types PDF error:", error);
      toast.error(
        t("vacations.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء ملف أنواع الإجازات",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          color: "white",
        }}
      >
        <div>{t("vacations.loading")}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
      }}
    >
      {/* العنوان */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            margin: 0,
            textAlign,
          }}
        >
          {t("vacations.title")}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexDirection: isRTL ? "row-reverse" : "row",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "عدد السجلات" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, perPageInput - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={filteredData?.length || 1000}
                value={perPageInput}
                onChange={e => {
                  const value = parseInt(e.target.value, 10) || 1;
                  const maxValue = filteredData?.length || 1000;
                  setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const maxValue = filteredData?.length || 1000;
                  const newValue = Math.min(maxValue, perPageInput + 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
          <button
            onClick={handleAdd}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaPlus />
            {t("vacations.addButton")}
          </button>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaEye />{" "}
            {t("vacations.preview.all", { defaultValue: "عرض PDF للكل" })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: isGeneratingPdf ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
              opacity: isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint />
            {isGeneratingPdf
              ? t("vacations.print.generating", {
                  defaultValue: "جاري الطباعة...",
                })
              : t("vacations.print.all", { defaultValue: "طباعة PDF للكل" })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaFileExcel />{" "}
            {t("vacations.export.all", { defaultValue: "تصدير Excel للكل" })}
          </button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #333",
        }}
      >
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder={t("vacations.searchPlaceholder", {
              defaultValue: "بحث سريع عن نوع الإجازة",
            })}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: "200px",
              padding: isRTL ? "12px 15px 12px 15px" : "12px 15px 12px 15px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              direction: dir,
              textAlign,
            }}
          />
          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            {[
              {
                value: "all",
                label: t("common.all", { defaultValue: "الكل" }),
              },
              {
                value: "active",
                label: t("vacations.status.active", { defaultValue: "نشط" }),
              },
              {
                value: "inactive",
                label: t("vacations.status.inactive", {
                  defaultValue: "غير نشط",
                }),
              },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                style={{
                  backgroundColor:
                    statusFilter === opt.value ? "#AC2000" : "#1a1f2e",
                  color: "white",
                  border: `1px solid ${statusFilter === opt.value ? "#AC2000" : "#444"}`,
                  padding: "8px 14px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: statusFilter === opt.value ? "bold" : "normal",
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            gap: "12px",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              color: "white",
              fontSize: 13,
              direction: dir,
            }}
          >
            <span>{t("common.sortBy", { defaultValue: "ترتيب حسب" })}</span>
            <select
              value={sortField}
              onChange={e => setSortField(e.target.value)}
              style={{
                padding: "10px 12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                minWidth: "160px",
              }}
            >
              <option value="name">
                {t("vacations.table.name", { defaultValue: "الاسم" })}
              </option>
              <option value="default_balance">
                {t("vacations.table.defaultBalance", {
                  defaultValue: "الرصيد الافتراضي",
                })}
              </option>
              <option value="is_active">
                {t("vacations.table.status", { defaultValue: "الحالة" })}
              </option>
            </select>
          </label>
          <SortOrderSelect
            value={sortOrder}
            onChange={setSortOrder}
            style={{ color: "white", fontSize: 13 }}
          />
        </div>
        {filteredData && filteredData.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "800px",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #333" }}>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("vacations.table.number")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("vacations.table.name")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("vacations.table.defaultBalance")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("vacations.table.status")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("vacations.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaveTypes.map((leaveType, index) => {
                  const total = filteredData.length;
                  const serialNumber =
                    sortOrder === "desc"
                      ? total - (localCurrentPage - 1) * perPage - index
                      : (localCurrentPage - 1) * perPage + index + 1;
                  return (
                    <tr
                      key={leaveType.id}
                      style={{ borderBottom: "1px solid #333" }}
                    >
                      <td
                        style={{
                          padding: "15px",
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        {serialNumber}
                      </td>
                      <td
                        style={{
                          padding: "15px",
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        {leaveType.name}
                      </td>
                      <td
                        style={{
                          padding: "15px",
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        {leaveType.default_balance} {t("vacations.days")}
                      </td>
                      <td
                        style={{
                          padding: "15px",
                          textAlign: "center",
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: leaveType.is_active
                              ? "#28a745"
                              : "#dc3545",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            minWidth: "60px",
                            display: "inline-block",
                          }}
                        >
                          {leaveType.is_active
                            ? t("vacations.status.active")
                            : t("vacations.status.inactive")}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "15px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            flexWrap: "nowrap",
                          }}
                        >
                          <button
                            onClick={() => handlePreviewSingle(leaveType)}
                            style={{
                              backgroundColor: "#17a2b8",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "13px",
                              flexShrink: 0,
                            }}
                            title={t("vacations.preview.all", {
                              defaultValue: "عرض PDF",
                            })}
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleEdit(leaveType.id)}
                            style={{
                              backgroundColor: "#ffc107",
                              color: "black",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "13px",
                              flexShrink: 0,
                            }}
                            title={t("vacations.actions.edit")}
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(leaveType.id)}
                            style={{
                              backgroundColor: "#34495e",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title={t("vacations.actions.toggleStatus", {
                              defaultValue: "تغيير الحالة",
                            })}
                          >
                            {leaveType.is_active ? (
                              <FaToggleOn />
                            ) : (
                              <FaToggleOff />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(leaveType)}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title={t("vacations.actions.delete")}
                          >
                            <FaTrash />
                          </button>
                          <button
                            onClick={() => handlePrintLeaveType(leaveType)}
                            disabled={isGeneratingPdf}
                            style={{
                              backgroundColor: isGeneratingPdf
                                ? "#666"
                                : "#17a2b8",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: isGeneratingPdf
                                ? "not-allowed"
                                : "pointer",
                              fontSize: "13px",
                              opacity: isGeneratingPdf ? 0.6 : 1,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title={t("vacations.print.single", {
                              defaultValue: "طباعة",
                            })}
                          >
                            <FaPrint />
                          </button>
                          <button
                            onClick={() =>
                              handleExportLeaveTypeToExcel(leaveType)
                            }
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title={t("vacations.export.single", {
                              defaultValue: "تصدير Excel",
                            })}
                          >
                            <FaFileExcel />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            {t("vacations.noRecords")}
          </div>
        )}

        {/********pagination ******** */}
        {totalPages > 0 && (
          <div
            style={{
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderTop: "1px solid #333",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() =>
                setLocalCurrentPage(Math.max(1, localCurrentPage - 1))
              }
              disabled={localCurrentPage === 1}
              style={{
                backgroundColor: localCurrentPage === 1 ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor: localCurrentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              {t("vacations.pagination.previous", { defaultValue: "السابق" })}
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setLocalCurrentPage(page)}
                style={{
                  backgroundColor:
                    page === localCurrentPage ? "#AC2000" : "#1a1f2e",
                  color: "white",
                  border: "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: page === localCurrentPage ? "bold" : "normal",
                }}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setLocalCurrentPage(Math.min(totalPages, localCurrentPage + 1))
              }
              disabled={localCurrentPage === totalPages}
              style={{
                backgroundColor:
                  localCurrentPage === totalPages ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor:
                  localCurrentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              {t("vacations.pagination.next", { defaultValue: "التالي" })}
            </button>
          </div>
        )}
      </div>

      {/*******delete modal *********/}
      {showDeleteModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                padding: "30px",
                borderRadius: "12px",
                border: "1px solid #333",
                maxWidth: "400px",
                width: "90%",
                textAlign: "center",
                direction: dir,
              }}
            >
              <h3
                style={{
                  color: "white",
                  marginBottom: "20px",
                  fontSize: "18px",
                  textAlign,
                }}
              >
                {t("vacations.deleteModal.title")}
              </h3>
              <p
                style={{
                  color: "#ccc",
                  marginBottom: "30px",
                  fontSize: "14px",
                  textAlign,
                }}
              >
                {t("vacations.deleteModal.message", {
                  name: leaveTypeToDelete?.name,
                })}
              </p>
              <p
                style={{
                  color: "white",
                  marginBottom: "30px",
                  fontSize: "12px",
                  textAlign,
                }}
              >
                {t("vacations.deleteModal.warning")}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "center",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <button
                  onClick={cancelDelete}
                  style={{
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  {t("vacations.deleteModal.cancel")}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  {t("vacations.deleteModal.confirm")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Print Templates */}
      {/* Single Leave Type Print Template */}
      {leaveTypeForPrint && pdfType === "single" && (
        <div
          id={`leave-type-pdf-${leaveTypeForPrint.id}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            minHeight: "1123px",
            height: "auto",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              minHeight: "1043px",
              height: "auto",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />

                <div
                  style={{
                    textAlign: "center",
                    padding: "6px 0 8px",
                    borderBottom: "1px solid #ddd",
                    marginBottom: "4px",
                    flexShrink: 0,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "900",
                      color: "#000080",
                    }}
                  >
                    {isRTL ? "تقرير الإجازات" : "Vacations Report"}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      marginTop: "3px",
                    }}
                  >
                    ID: #{leaveTypeForPrint.id}
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      border: "2px solid #000",
                      borderRadius: "4px",
                      overflow: "hidden",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {[
                      {
                        label: t("vacations.print.labels.name", {
                          defaultValue: "اسم نوع الإجازة",
                        }),
                        value: leaveTypeForPrint.name || "—",
                      },
                      {
                        label: t("vacations.print.labels.defaultBalance", {
                          defaultValue: "الرصيد الافتراضي",
                        }),
                        value: `${leaveTypeForPrint.default_balance || 0} ${t("vacations.days")}`,
                        accent: true,
                      },
                      {
                        label: t("vacations.print.labels.status", {
                          defaultValue: "الحالة",
                        }),
                        value: leaveTypeForPrint.is_active
                          ? t("vacations.status.active")
                          : t("vacations.status.inactive"),
                        accent: true,
                      },
                    ]
                      .filter(row => row.value && row.value !== "—")
                      .map(row => (
                        <div
                          key={row.label}
                          style={{
                            display: "flex",
                            borderBottom: "1px solid #000",
                            minHeight: "38px",
                          }}
                        >
                          <div
                            style={{
                              width: "30%",
                              borderLeft: "1px solid #000",
                              backgroundColor: "rgba(227, 242, 253, 0.4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              textAlign: isRTL ? "right" : "left",
                              fontWeight: "700",
                              fontSize: "14px",
                              direction: isRTL ? "rtl" : "ltr",
                              color: "#1976d2",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                              padding: "0 10px",
                            }}
                          >
                            {row.label}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              textAlign: isRTL ? "right" : "left",
                              fontSize: "15px",
                              fontWeight: row.accent ? "700" : "500",
                              color: row.accent ? "#0057c2" : "#000",
                              direction: isRTL ? "rtl" : "ltr",
                              padding: "0 10px",
                              backgroundColor: "rgba(255, 255, 255, 0.3)",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                            }}
                          >
                            {row.value}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Leave Types Print Template */}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`leave-types-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  padding: "18px 18px 10px",
                  position: "relative",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: isRTL ? "rtl" : "ltr",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "1033px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "60%",
                    backgroundPosition: "center top",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.05,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      display: "block",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />

                  <div
                    style={{
                      textAlign: "center",
                      padding: "6px 0 8px",
                      borderBottom: "1px solid #ddd",
                      marginBottom: "4px",
                      flexShrink: 0,
                      direction: isRTL ? "rtl" : "ltr",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "900",
                        color: "#000080",
                      }}
                    >
                      {isRTL ? "تقرير الإجازات" : "Vacations Report"}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "3px",
                      }}
                    >
                      {isRTL ? "تاريخ التقرير" : "Report Date"}:{" "}
                      {new Date().toLocaleDateString("en-US")} —{" "}
                      {isRTL ? "صفحة" : "Page"} {chunkIndex + 1} /{" "}
                      {pdfChunks.length}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "12px",
                      direction: isRTL ? "rtl" : "ltr",
                      fontFamily: '"Times New Roman", "Times", serif',
                      flex: 1,
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "11px",
                        border: "1px solid #000",
                        fontFamily: '"Times New Roman", "Times", serif',
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "700",
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                              width: "5%",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "700",
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {t("vacations.table.name", {
                              defaultValue: "اسم نوع الإجازة",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "700",
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {t("vacations.table.defaultBalance", {
                              defaultValue: "الرصيد الافتراضي",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "700",
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {t("vacations.table.status", {
                              defaultValue: "الحالة",
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {chunk.map((leaveType, idx) => {
                          const total = filteredData.length;
                          const globalIdx = filteredData.findIndex(
                            lt => String(lt?.id) === String(leaveType?.id),
                          );
                          const safeGlobalIdx = globalIdx >= 0 ? globalIdx : 0;
                          const serialNumber =
                            sortOrder === "desc"
                              ? total - safeGlobalIdx
                              : safeGlobalIdx + 1;
                          return (
                            <tr
                              key={leaveType.id}
                              style={{
                                backgroundColor:
                                  idx % 2 === 0
                                    ? "rgba(255, 255, 255, 0.7)"
                                    : "rgba(245, 245, 245, 0.5)",
                              }}
                            >
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "5px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                }}
                              >
                                {serialNumber}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "5px",
                                  direction: isRTL ? "rtl" : "ltr",
                                  textAlign: isRTL ? "right" : "left",
                                  fontSize: "10px",
                                }}
                              >
                                {leaveType.name || "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "5px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                }}
                              >
                                {leaveType.default_balance || 0}{" "}
                                {t("vacations.days")}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "5px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                }}
                              >
                                {leaveType.is_active
                                  ? t("vacations.status.active")
                                  : t("vacations.status.inactive")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                        objectFit: "contain",
                        display: "block",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t("vacations.title", { defaultValue: "أنواع الإجازات" })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
          setPdfType(null);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {pdfChunks &&
            pdfChunks.length > 0 &&
            pdfChunks.map((chunk, chunkIdx) => (
              <div
                key={chunkIdx}
                style={{
                  width: "794px",
                  height: "1123px",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  padding: "40px",
                  boxSizing: "border-box",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "5px solid #000",
                    padding: "3px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "1043px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      border: "2px solid #000",
                      padding: "18px 18px 10px",
                      position: "relative",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      direction: isRTL ? "rtl" : "ltr",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "100%",
                      height: "1033px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "60%",
                        backgroundPosition: "center top",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.05,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "8px",
                          borderBottom: "4px double #000",
                          paddingBottom: "8px",
                          display: "block",
                          flexShrink: 0,
                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          textAlign: "center",
                          padding: "6px 0 8px",
                          borderBottom: "1px solid #ddd",
                          marginBottom: "4px",
                          direction: isRTL ? "rtl" : "ltr",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "900",
                            color: "#000080",
                          }}
                        >
                          {isRTL ? "تقرير الإجازات" : "Vacations Report"}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            marginTop: "3px",
                          }}
                        >
                          {isRTL ? "تاريخ التقرير" : "Report Date"}:{" "}
                          {new Date().toLocaleDateString("en-US")} —{" "}
                          {isRTL ? "صفحة" : "Page"} {chunkIdx + 1} /{" "}
                          {pdfChunks.length}
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: "12px",
                          direction: isRTL ? "rtl" : "ltr",
                          fontFamily: '"Times New Roman", "Times", serif',
                          flex: 1,
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "11px",
                            border: "1px solid #000",
                            fontFamily: '"Times New Roman", "Times", serif',
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  backgroundColor: "rgba(238, 242, 247, 0.7)",
                                  width: "5%",
                                }}
                              >
                                #
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  backgroundColor: "rgba(238, 242, 247, 0.7)",
                                }}
                              >
                                {t("vacations.table.name", {
                                  defaultValue: "اسم نوع الإجازة",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  backgroundColor: "rgba(238, 242, 247, 0.7)",
                                }}
                              >
                                {t("vacations.table.defaultBalance", {
                                  defaultValue: "الرصيد الافتراضي",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  backgroundColor: "rgba(238, 242, 247, 0.7)",
                                }}
                              >
                                {t("vacations.table.status", {
                                  defaultValue: "الحالة",
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((leaveType, idx) => {
                              const total = filteredData.length;
                              const globalIdx = filteredData.findIndex(
                                lt => String(lt?.id) === String(leaveType?.id),
                              );
                              const safeGlobalIdx =
                                globalIdx >= 0 ? globalIdx : 0;
                              const serialNumber =
                                sortOrder === "desc"
                                  ? total - safeGlobalIdx
                                  : safeGlobalIdx + 1;
                              return (
                                <tr
                                  key={leaveType.id}
                                  style={{
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255,255,255,0.7)"
                                        : "rgba(245,245,245,0.5)",
                                  }}
                                >
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "5px",
                                      textAlign: "center",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {serialNumber}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "5px",
                                      direction: isRTL ? "rtl" : "ltr",
                                      textAlign: isRTL ? "right" : "left",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {leaveType.name || "—"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "5px",
                                      textAlign: "center",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {leaveType.default_balance || 0}{" "}
                                    {t("vacations.days", {
                                      defaultValue: "يوم",
                                    })}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "5px",
                                      textAlign: "center",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {leaveType.is_active
                                      ? t("vacations.status.active", {
                                          defaultValue: "نشط",
                                        })
                                      : t("vacations.status.inactive", {
                                          defaultValue: "غير نشط",
                                        })}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "10px",
                          left: "18px",
                          right: "18px",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                            borderTop: "4px double #000",
                            paddingTop: "8px",
                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </PdfPreviewModal>
      {/* Single Leave Type Preview Modal */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && !!singleLeaveTypeForPreview}
        title={t("vacations.table.name", { defaultValue: "نوع الإجازة" })}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setSingleLeaveTypeForPreview(null);
        }}
        onPrint={() => {
          if (singleLeaveTypeForPreview) {
            handlePrintLeaveType(singleLeaveTypeForPreview);
          }
        }}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {singleLeaveTypeForPreview && (
            <div
              style={{
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  minHeight: "1043px",
                  height: "auto",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    minHeight: "1033px",
                    height: "1033px",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    direction: isRTL ? "rtl" : "ltr",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.08,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "visible",
                        minHeight: 0,
                        paddingBottom: "120px",
                      }}
                    >
                      <div
                        style={{
                          marginTop: "12px",
                          border: "2px solid #000",
                          borderRadius: "4px",
                          overflow: "hidden",
                          fontFamily:
                            '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: "bold",
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        }}
                      >
                        {[
                          {
                            label: t("vacations.print.labels.name", {
                              defaultValue: "اسم نوع الإجازة",
                            }),
                            value: singleLeaveTypeForPreview.name || "—",
                          },
                          {
                            label: t("vacations.print.labels.defaultBalance", {
                              defaultValue: "الرصيد الافتراضي",
                            }),
                            value: `${singleLeaveTypeForPreview.default_balance || 0} ${t("vacations.days")}`,
                            accent: true,
                          },
                          {
                            label: t("vacations.print.labels.status", {
                              defaultValue: "الحالة",
                            }),
                            value: singleLeaveTypeForPreview.is_active
                              ? t("vacations.status.active")
                              : t("vacations.status.inactive"),
                            accent: true,
                          },
                        ]
                          .filter(row => row.value && row.value !== "—")
                          .map(row => (
                            <div
                              key={row.label}
                              style={{
                                display: "flex",
                                borderBottom: "1px solid #000",
                                minHeight: "38px",
                              }}
                            >
                              <div
                                style={{
                                  width: "30%",
                                  borderLeft: "1px solid #000",
                                  backgroundColor: "rgba(227, 242, 253, 0.4)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  textAlign: isRTL ? "right" : "left",
                                  fontWeight: "700",
                                  fontSize: "14px",
                                  direction: isRTL ? "rtl" : "ltr",
                                  color: "#1976d2",
                                  fontFamily:
                                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                  padding: "0 10px",
                                }}
                              >
                                {row.label}
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  textAlign: isRTL ? "right" : "left",
                                  fontSize: "15px",
                                  fontWeight: row.accent ? "700" : "500",
                                  color: row.accent ? "#0057c2" : "#000",
                                  direction: isRTL ? "rtl" : "ltr",
                                  padding: "0 10px",
                                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                                  fontFamily:
                                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                }}
                              >
                                {row.value}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "18px",
                        right: "18px",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </PdfPreviewModal>
      {/* Hidden Measurement Table */}
      <div
        id="vacations-measurement-container"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "658px",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
            border: "1px solid #000",
            fontFamily: '"Times New Roman", "Times", serif',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
              <th
                style={{
                  border: "1px solid #000",
                  padding: "6px",
                  textAlign: "center",
                  fontWeight: "700",
                  width: "5%",
                }}
              >
                #
              </th>
              <th
                style={{
                  border: "1px solid #000",
                  padding: "6px",
                  textAlign: "center",
                  fontWeight: "700",
                }}
              >
                {t("vacations.table.name", { defaultValue: "اسم نوع الإجازة" })}
              </th>
              <th
                style={{
                  border: "1px solid #000",
                  padding: "6px",
                  textAlign: "center",
                  fontWeight: "700",
                }}
              >
                {t("vacations.table.defaultBalance", {
                  defaultValue: "الرصيد الافتراضي",
                })}
              </th>
              <th
                style={{
                  border: "1px solid #000",
                  padding: "6px",
                  textAlign: "center",
                  fontWeight: "700",
                }}
              >
                {t("vacations.table.status", { defaultValue: "الحالة" })}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((leaveType, idx) => (
              <tr key={leaveType.id}>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "5px",
                    textAlign: "center",
                    fontSize: "10px",
                  }}
                >
                  {idx + 1}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "5px",
                    direction: isRTL ? "rtl" : "ltr",
                    textAlign: isRTL ? "right" : "left",
                    fontSize: "10px",
                  }}
                >
                  {leaveType.name || "—"}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "5px",
                    textAlign: "center",
                    fontSize: "10px",
                  }}
                >
                  {leaveType.default_balance || 0} {t("vacations.days")}
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "5px",
                    textAlign: "center",
                    fontSize: "10px",
                  }}
                >
                  {leaveType.is_active
                    ? t("vacations.status.active")
                    : t("vacations.status.inactive")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Vacations;
